import { useQuery as useApolloQuery } from '@apollo/client';
import { useQuery } from '@tanstack/react-query';
import type { TRPCClientErrorLike } from '@trpc/client';
import { useCallback, useMemo } from 'react';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';
import { useTRPC } from 'src/utils/trpc';
import { mapTRPCRefetch } from 'src/utils/trpcUtils';

import { useErrorNotification } from '@/hooks/useErrorNotification';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import type { QueryHookConfig, QueryHookFactoryResult } from './types';

/**
 * Permission gate for the GraphQL branch. When a hook declares
 * `graphqlPermission`, we must consult `useHasPermissionQuery`; when it
 * doesn't, we must NOT call that hook at all (it would add needless permission
 * lookups to every migrated read). `graphqlPermission` is fixed at
 * factory-creation time, so selecting the implementation once here keeps the
 * set of hooks called on every render stable — Rules of Hooks safe.
 */
const ALWAYS_PERMITTED = { hasPermission: true, loading: false } as const;
/**
 * Factory function that creates a GraphQL-to-TRPC wrapper hook.
 *
 * This reduces boilerplate by generating hooks that:
 * - Switch between GraphQL and TRPC based on the 'trpc' feature flag
 * - Handle error notifications automatically
 * - Map TRPC responses to GraphQL query structures
 * - Provide consistent return types
 */
export function createQueryHook<
  TQueryArgs,
  TTRPCOutput,
  TGraphQLData extends { __typename?: 'query_root' },
>(
  config: QueryHookConfig<TQueryArgs, TTRPCOutput, TGraphQLData>
): QueryHookFactoryResult<TQueryArgs, TGraphQLData> {
  const {
    trpcQueryOptions,
    mapTrpcDataToGraphQL,
    graphqlDocument,
    graphqlVariables,
    graphqlFetchPolicy = 'no-cache',
    trpcStaleTime,
    trpcRefetchInterval,
    mapGraphQLData,
    graphqlPermission,
    suppressErrorNotification = false,
  } = config;

  // Selected once per factory (graphqlPermission is static config), so the same
  // hook shape runs on every render of the returned hook.
  //
  // `canHaveAccessAsContributor: true` is deliberate: the only thing this gate
  // needs to know is whether Hasura will expose the table's `query_root` field
  // to this role AT ALL — which is true whenever the role has ANY read grant on
  // the type (owner/contributor/any), regardless of the RLS ContributorType.
  // Without this flag, a Standard user (whose grant is owner/contributor-scoped,
  // not `any`) would be treated as unpermitted and have the query wrongly
  // skipped, even though Hasura would happily return their RLS-filtered rows.
  const useGraphqlPermissionGate = graphqlPermission
    ? () => useHasPermissionQuery(graphqlPermission, undefined, true)
    : () => ALWAYS_PERMITTED;

  return ({ queryArgs, shouldSkip, fetchPolicy }) => {
    const skip = shouldSkip ?? false;
    const trpcEnabled = useIsFeatureFlagEnabled('trpc');
    const trpc = useTRPC();

    // TRPC Query
    const {
      data: trpcData,
      isPending: trpcLoading,
      refetch: trpcRefetch,
      error: trpcError,
    } = useQuery<
      TTRPCOutput,
      TRPCClientErrorLike<{ transformer: true; errorShape: unknown }>
    >({
      ...trpcQueryOptions(trpc, queryArgs),
      enabled: trpcEnabled && !skip,
      staleTime: trpcStaleTime,
      refetchInterval: trpcRefetchInterval
        ? (query) => trpcRefetchInterval(query.state.data)
        : undefined,
    });

    useErrorNotification(trpcError, trpcEnabled && !suppressErrorNotification);

    // Transform TRPC data to match GraphQL structure
    const mappedTrpcData = useMemo(() => {
      if (!trpcData) {
        return undefined;
      }

      return mapTrpcDataToGraphQL(trpcData) as TGraphQLData;
    }, [trpcData]);

    // Memoized refetch functions to prevent unnecessary re-renders
    const trpcRefetchMapped = useCallback(async () => {
      const result = await mapTRPCRefetch(trpcRefetch, mapTrpcDataToGraphQL);

      return result as {
        data: TGraphQLData | undefined;
        error: TRPCClientErrorLike<{
          transformer: true;
          errorShape: unknown;
        }> | null;
      };
    }, [trpcRefetch]);

    // GraphQL Query
    const graphqlVars = graphqlVariables && graphqlVariables(queryArgs);

    // Read-permission gate for the GraphQL branch only (see `graphqlPermission`
    // in QueryHookConfig). Skip while the check is resolving so we never fire
    // against a role that can't select the table (RSP-6542).
    const {
      hasPermission: graphqlPermitted,
      loading: graphqlPermissionLoading,
    } = useGraphqlPermissionGate();
    const resolvedGraphqlFetchPolicy = fetchPolicy ?? graphqlFetchPolicy;

    const {
      data: graphqlData,
      loading: graphqlLoading,
      refetch: graphqlRefetch,
      error: graphqlError,
    } = useApolloQuery(graphqlDocument, {
      variables: graphqlVars,
      fetchPolicy: resolvedGraphqlFetchPolicy,
      skip:
        trpcEnabled || skip || graphqlPermissionLoading || !graphqlPermitted,
    });

    useErrorNotification(
      graphqlError,
      !trpcEnabled && !suppressErrorNotification
    );

    // Transform GraphQL data if mapper provided
    const mappedGraphqlData = useMemo(
      () =>
        graphqlData && mapGraphQLData
          ? mapGraphQLData(graphqlData, queryArgs)
          : graphqlData,
      [graphqlData, queryArgs]
    );

    // Memoized refetch functions to prevent unnecessary re-renders
    const graphqlRefetchMapped = useCallback(async () => {
      const { data: refetchData, error, loading } = await graphqlRefetch();
      const mappedData =
        refetchData && mapGraphQLData
          ? mapGraphQLData(refetchData, queryArgs)
          : refetchData;

      return { data: mappedData, error: error ?? null, loading };
    }, [graphqlRefetch, queryArgs]);

    // Return appropriate data based on feature flag
    if (trpcEnabled) {
      return {
        // `isPending` (no data yet) rather than `isLoading` closes the
        // enabled-but-idle window where an enabled query briefly reports
        // "loaded, no data". But a skipped query is intentionally disabled and
        // stays `pending`/`idle` forever, so treat skip as "not loading".
        loading: skip ? false : trpcLoading,
        data: mappedTrpcData,
        refetch: trpcRefetchMapped,
        error: trpcError,
      };
    }

    return {
      loading: graphqlLoading,
      data: mappedGraphqlData,
      refetch: graphqlRefetchMapped,
      error: graphqlError,
    };
  };
}
